<template>
  <div class="stepper-wrapper">
    <v-stepper-vertical hide-actions :model-value="currentStep">
      <template v-slot:default>
        <allow-security-awareness-step-select-service
          :current-step="currentStep"
          :model-value="localValue"
          :has-existing-connection="hasExistingConnection"
          @update:modelValue="updateLocalValue"
          @next="goToNextStep"
          @dismissed="closeModal"
        />
        <allow-security-awareness-step-allowlist-guide
          :current-step="currentStep"
          :model-value="localValue"
          :disable="disable"
          @next="goToNextStep"
          @back="goToPreviousStep"
        />
        <allow-security-awareness-step-test-email
          :current-step="currentStep"
          :model-value="localValue"
          @update:modelValue="updateLocalValue"
          @next="goToNextStep"
          @back="goToPreviousStep"
        />
        <allow-security-awareness-step-confirm-email
          :current-step="currentStep"
          :model-value="localValue"
          @next="goToNextStep"
          @back="goToPreviousStep"
        />
        <allow-security-awareness-step-assign-users
          :current-step="currentStep"
          :model-value="localValue"
          @update:modelValue="updateLocalValue"
          @next="goToNextStep"
          @back="goToPreviousStep"
        />
      </template>
    </v-stepper-vertical>
    <div v-if="currentStep > 4">
      <allow-security-awareness-final-step
        :model-value="localValue"
        :disable="disable"
        @dismissed="closeModal"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { type PropType, defineComponent, ref } from "vue";
import { AccessPermissionType } from "@/constants/cloud-apps.ts";
import { isSATCustomDomain } from "@/constants/security-awareness.ts";
import { useAccountStore } from "@/_store/account.module.ts";
import type { SatConnection } from "@/_store/security-awareness/security-awareness.adapters.ts";
import type { DialogDataConfig } from "@/_store/dialogs.module.ts";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";
import AllowSecurityAwarenessStepSelectService from "@/components/modals/security-awareness/AllowSecurityAwarenessStepSelectService.vue";
import AllowSecurityAwarenessStepAllowlistGuide from "@/components/modals/security-awareness/AllowSecurityAwarenessStepAllowlistGuide.vue";
import AllowSecurityAwarenessStepTestEmail from "@/components/modals/security-awareness/AllowSecurityAwarenessStepTestEmail.vue";
import AllowSecurityAwarenessStepConfirmEmail from "@/components/modals/security-awareness/AllowSecurityAwarenessStepConfirmEmail.vue";
import AllowSecurityAwarenessStepAssignUsers from "@/components/modals/security-awareness/AllowSecurityAwarenessStepAssignUsers.vue";
import AllowSecurityAwarenessFinalStep from "@/components/modals/security-awareness/AllowSecurityAwarenessFinalStep.vue";

export default defineComponent({
  components: {
    AllowSecurityAwarenessStepSelectService,
    AllowSecurityAwarenessStepAllowlistGuide,
    AllowSecurityAwarenessStepTestEmail,
    AllowSecurityAwarenessStepConfirmEmail,
    AllowSecurityAwarenessStepAssignUsers,
    AllowSecurityAwarenessFinalStep,
  },
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<SatConnection>>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["dismissed"],
  setup(props, { emit }) {
    const currentStep = ref(props.config.item.step || 0);
    const { email: defaultAccountEmail } = useAccountStore();
    const testEmailAddresses = props.config.item.testEmailAddresses || [];
    const localValue = ref<SatConnectionConfigForm>({
      selectedService: props.config.item.name
        ? isSATCustomDomain(props.config.item.name)
          ? "other"
          : props.config.item.name
        : "",
      domain: isSATCustomDomain(props.config.item.name) ? props.config.item.name : "",
      customEmail: testEmailAddresses[0] === defaultAccountEmail ? "" : testEmailAddresses[0],
      userType: props.config.item.userType || AccessPermissionType.ALL_USERS,
      groups: props.config.item.groups || [],
      users: props.config.item.users || [],
      labels: props.config.item.labels || [],
      includeUsers: !!props.config.item.users?.length,
      includeGroups: !!props.config.item.groups?.length,
      includeLabels: !!props.config.item.labels?.length,
    });
    const closeModal = () => {
      emit("dismissed");
    };

    const hasExistingConnection = Boolean(props.config.item.name);

    const updateLocalValue = (value: SatConnectionConfigForm) => {
      localValue.value = value;
    };

    const goToNextStep = () => {
      currentStep.value = currentStep.value + 1;
    };

    const goToPreviousStep = () => {
      currentStep.value = currentStep.value - 1;
    };

    return {
      currentStep,
      closeModal,
      localValue,
      updateLocalValue,
      goToNextStep,
      goToPreviousStep,
      hasExistingConnection,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(.v-stepper-vertical__item:not(:last-child)) {
  .v-stepper-vertical__item:not(:last-child)::after {
    border-left: 1px solid rgba(var(--v-theme-on-surface));
  }
}

.stepper-wrapper {
  height: calc(100vh - 205px);
  overflow: auto;
}

:deep(*) {
  .icon-check-xs:before {
    color: rgba(var(--v-theme-white));
  }

  .text-indigo-medium {
    .icon-event-detail-s:before {
      color: rgb(var(--v-theme-indigo-medium));
    }
  }

  .v-stepper-vertical-item__avatar.v-avatar {
    background: rgb(var(--v-theme-indigo-pale));
    color: rgb(var(--v-theme-primary));
  }

  .v-expansion-panel--active .v-stepper-vertical-item__avatar.v-avatar {
    background: rgb(var(--v-theme-indigo-medium));
    color: rgb(var(--v-theme-white));
  }

  .step-completed {
    .v-avatar {
      background: rgb(var(--v-theme-success)) !important;
    }
  }

  .step-error {
    .v-avatar {
      background: rgb(var(--v-theme-red-dark)) !important;
    }

    .step-title {
      color: rgb(var(--v-theme-red-dark)) !important;
    }
  }

  .info-alert .icon:before {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

:deep(.hide-step) {
  .v-expansion-panel-text {
    display: none;
  }
}
</style>
